import React from 'react';
import PropTypes from 'prop-types';
import { Motion, spring, presets } from 'react-motion';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';

import { getScrollTop, getScrollParent } from '../../../../shared/utils/win';

import {
    BB_BETFEED_PULL_REFRESH_CSS_BLOCK_NAME as BLOCK_NAME,
    BB_BETFEED_PULL_REFRESH_DEFAULT_DISTANCE_THRESHOLD,
} from '../../constants';

const beginning = 0, zero = 0, latest = 0, ratio = 0.4, minTop = 1, minDiff = 5;

export default class PullToRefresh extends React.Component {

    constructor(props) {
        super(props);

        this.touchStart = this.touchStart.bind(this);
        this.touchEnd = this.touchEnd.bind(this);
        this.touchMove = this.touchMove.bind(this);
    }

    /**
     * Props:
     *   pullArea: locate the area can be pulled
     *   actions: start and end are triggered in pulling
     *   distance: threshold distance for triggering refreshing
     *
     */
    static propTypes = {
        state: PropTypes.string,
        pullArea: PropTypes.shape({
            element: PropTypes.object,
            selector: PropTypes.string,
        }).isRequired,
        actions: PropTypes.shape({
            start: PropTypes.func,
            pulling: PropTypes.func,
            end: PropTypes.func,
            refresh: PropTypes.func.isRequired,
        }),
        distance: PropTypes.shape({
            threshold: PropTypes.number.isRequired,
        }).isRequired,
        children: PropTypes.object.isRequired,
    }

    static defaultProps = {
        actions: {},
        distance: {
            threshold: BB_BETFEED_PULL_REFRESH_DEFAULT_DISTANCE_THRESHOLD,
        },
    }

    _pullAreaDOM = null;
    _scrollParent = null;

    pullState = {
        start: null,
        refreshing: false,
    };

    state = {
        pullDistance: zero,
    }

    get refreshable() {
        return this.state.pullDistance >= this.props.distance.threshold;
    }

    get scrollTop() {
        return getScrollTop(this._scrollParent);
    }

    get pullAreaDOM() {
        return this._pullAreaDOM;
    }

    /**
     * Automatically unbind old touch events when pullAreaDOM is changed.
     *
     * @param {HTMLElement} element new pullAreaDOM
     */
    set pullAreaDOM(element) {
        if (this._pullAreaDOM !== element) {
            this.disablePTR();
            this._pullAreaDOM = element;
            if (element) {
                this.enablePTR();
                this._scrollParent = getScrollParent(element);
            }
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(
            omit(nextProps, ['actions']),
            omit(this.props, ['actions']),
        ) || !isEqual(nextState, this.state);
    }

    componentDidUpdate(prevProps) {
        if (!isEqual(prevProps.pullArea, this.props.pullArea)) {
            this.componentDidMount();
        }
    }

    componentDidMount() {
        const pullArea = this.props.pullArea;
        this.pullAreaDOM = pullArea.element || document.querySelector(pullArea.selector);
    }

    render() {
        const stateClassName = this.props.state ? ` ${BLOCK_NAME}--${this.props.state}` : '';

        return (
            <Motion style={{ y: spring(this.state.pullDistance, presets.stiff) }}>
                {({ y }) => {
                    const style = y === zero ? {} : {
                        transform: `translate3d(0, ${y}px, 0)`,
                        WebkitTransform: `translate3d(0, ${y}px, 0)`,
                    };

                    return (
                        <div className={`${BLOCK_NAME}${stateClassName}`} style={style}>
                            {this.props.children}
                        </div>
                    );
                }}
            </Motion>
        );
    }

    componentWillUnmount() {
        this.pullAreaDOM = null;
    }

    enablePTR() {
        const pullArea = this._pullAreaDOM;
        if (pullArea) {
            pullArea.addEventListener('touchstart', this.touchStart);
            pullArea.addEventListener('touchcancel', this.touchEnd);
            pullArea.addEventListener('touchend', this.touchEnd);
            pullArea.addEventListener('touchmove', this.touchMove, false, {passive: false});
        }
    }

    disablePTR() {
        this.pullState = {};
        const pullArea = this._pullAreaDOM;
        if (pullArea) {
            pullArea.removeEventListener('touchstart', this.touchStart);
            pullArea.removeEventListener('touchcancel', this.touchEnd);
            pullArea.removeEventListener('touchend', this.touchEnd);
            pullArea.removeEventListener('touchmove', this.touchMove, false);
        }
    }

    touchStart(event) {
        this.pullState.start = event.touches[beginning].clientY - this.state.pullDistance / ratio;
        if (isFunction(this.props.actions.start)) {
            this.props.actions.start(this);
        }
    }

    /**
     * When moving down, if pull area has reached the viewport top then prevent the browser behavior.
     * And if the pull distance is greater than props.distance.threshold, then mark as refreshable,
     * otherwise if distance is less than props.distance.threshold or moving up, mark as no need to refresh
     *
     * @param {TouchEvent} event for touchmove
     * @returns {void}
     */
    touchMove(event) {
        const currentY = event.touches[latest].clientY;
        if (isUndefined(this.pullState.start)) {
            this.touchStart(event);
        }
        const diffY = currentY - this.pullState.start;
        if (Math.abs(diffY) > minDiff) {
            if (this.scrollTop < minTop && diffY > minDiff) {
                event.preventDefault();
                this.setState({ pullDistance: Math.round(ratio * diffY) });
                this.props.actions.pulling(this.refreshable);
            } else if (diffY < zero) {
                this.setState({ pullDistance: zero });
            }
        }
    }

    async touchEnd() {
        if (isFunction(this.props.actions.end)) {
            this.props.actions.end(this);
        }

        if(this.refreshable && !this.pullState.refreshing) {
            this.pullState.refreshing = true;
            this.setState({ pullDistance: this.props.distance.threshold });
            await this.props.actions.refresh();
            this.pullState.refreshing = false;
        }

        this.setState({ pullDistance: zero });
        this.pullState = {};
    }
}
